import React, { useState } from 'react';
import { Check, Copy, Plus, Search, Settings2, Share2 } from 'lucide-react';
import { CommunityOverview, CommunityPerson, FRIEND_INVITE_TEXTS, communityGroups, communityInitials } from '@radevents/shared';
import { People } from './CommunityParts';

/**
 * Kopf des Reiters Freunde in Meine Events (30.10.2026), Vorbilder Tonal und Story-Leisten.
 *
 * Eine Zeile Gesichter statt eines zugeklappten „Freunde verwalten“: vorn „Einladen“, dann die
 * Freunde mit Vornamen, rechts „Verwalten“. Offene Anfragen stehen darunter, weil sie eine
 * Antwort brauchen. Ohne Freunde steht statt der Leiste die Einladung (`FreundeLeer`).
 */
export function FreundeKopf({ data, busy, onAction, onEinladen, onVerwalten, verwaltenOffen }: {
  data: CommunityOverview;
  busy?: boolean;
  onAction: (person: CommunityPerson, action: 'request' | 'accept' | 'remove' | 'block') => void;
  onEinladen: () => void;
  onVerwalten: () => void;
  verwaltenOffen: boolean;
}) {
  const g = communityGroups(data.people);
  return <section className="fk" aria-label="Deine Freunde">
    <div className="fk-kopf">
      <h2 className="fk-titel">Freunde <span>{g.friends.length}</span></h2>
      <button type="button" className="fk-verwalten" aria-expanded={verwaltenOffen} onClick={onVerwalten}>
        <Settings2 size={16} aria-hidden />Verwalten
      </button>
    </div>
    <ul className="fk-leiste">
      <li>
        <button type="button" className="fk-person fk-person--neu" onClick={onEinladen}>
          <span className="fk-kreis"><Plus size={22} aria-hidden /></span>
          <span className="fk-name">Einladen</span>
        </button>
      </li>
      {g.friends.map(p => (
        <li key={p.id} className="fk-person" title={p.displayName}>
          <span className="fk-kreis">{communityInitials(p.displayName)}</span>
          <span className="fk-name">{p.displayName.trim().split(/\s+/)[0]}</span>
        </li>
      ))}
      {g.outgoing.map(p => (
        <li key={p.id} className="fk-person fk-person--angefragt" title={`${p.displayName}, angefragt`}>
          <span className="fk-kreis">{communityInitials(p.displayName)}</span>
          <span className="fk-name">angefragt</span>
        </li>
      ))}
    </ul>
    {g.incoming.length > 0 && (
      <div className="fk-anfragen">
        <h3>{g.incoming.length === 1 ? 'Eine Freundschaftsanfrage' : `${g.incoming.length} Freundschaftsanfragen`}</h3>
        <People people={g.incoming} busy={busy} onAction={onAction} />
      </div>
    )}
  </section>;
}

/** Ohne Freunde: Gesichter, ein Satz, Link teilen oder Benutzername suchen (Vorbild Peloton). */
export function FreundeLeer({ inviteUrl, onSuchen }: { inviteUrl?: string | null; onSuchen: () => void }) {
  const [kopiert, setKopiert] = useState(false);
  const kannTeilen = typeof navigator !== 'undefined' && typeof navigator.share === 'function'
    && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  const teilen = () => {
    if (!inviteUrl) return;
    if (kannTeilen) { navigator.share({ text: FRIEND_INVITE_TEXTS.shareMessage(inviteUrl) }).catch(() => {}); return; }
    navigator.clipboard?.writeText(inviteUrl).then(() => setKopiert(true)).catch(() => {});
  };
  return <section className="fl">
    <span className="fl-gesichter" aria-hidden><span>A</span><span>M</span><span>L</span></span>
    <h2 className="fl-titel">Fahrt zusammen</h2>
    <p className="fl-text">Lade Freunde ein. Dann siehst du hier, welche Events sie vorgemerkt haben und wo sie dabei sind. Sie sehen deine ebenso.</p>
    {inviteUrl && (
      <button type="button" className="fl-haupt" onClick={teilen}>
        {kannTeilen ? <><Share2 size={17} aria-hidden />{FRIEND_INVITE_TEXTS.share}</>
          : kopiert ? <><Check size={17} aria-hidden />Link kopiert</> : <><Copy size={17} aria-hidden />Einladungslink kopieren</>}
      </button>
    )}
    <button type="button" className="fl-zweit" onClick={onSuchen}><Search size={16} aria-hidden />Nach Benutzername suchen</button>
  </section>;
}
